// Arquitectura del sistema (Sistemas → Resumen, /sistemas/arquitectura).
// Documentación VIVA estilo C4/arc42: la narrativa y los diagramas son curados,
// los números (esquemas, tablas, funciones, pantallas, versión de PG) se leen en
// tiempo real de la BD vía autenticacion.sys_arquitectura_estado() (solo admins).
import { useQuery } from '@tanstack/react-query';
import { Boxes, Cpu, Database, Globe, Layers, Loader2, Network, Shield, Wifi, WifiOff, Workflow } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

interface EsquemaVivo { nombre: string; tablas: number; vistas: number; funciones: number }
interface MenuVivo { nombre: string; orden: number; submenus: number; pantallas: number }
interface Estado {
  postgres_version: string; generado_at: string;
  totales: { esquemas: number; tablas: number; funciones: number; pantallas: number; menus: number; empresas: number; usuarios: number };
  esquemas: EsquemaVivo[]; menus: MenuVivo[];
}

const num = (v: number | undefined) => Number(v ?? 0).toLocaleString(LOCALE);

// ---------- contenido curado ----------
const STACK: { capa: string; tec: string; detalle: string }[] = [
  { capa: 'Frontend', tec: 'React 18 + TypeScript 5 + Vite 5', detalle: 'SPA con pestañas keep-alive; Tailwind CSS + Radix UI; TanStack Query (estado de servidor) + Zustand' },
  { capa: 'Offline', tec: 'PWA + Service Worker + IndexedDB', detalle: 'App instalable que abre sin red; catálogo/clientes cacheados; cola de ventas idempotente; claves SRI generadas en el equipo' },
  { capa: 'API', tec: 'PostgREST', detalle: 'REST autogenerada desde la BD — sin backend intermedio; autenticación JWT; un header elige el esquema' },
  { capa: 'Base de datos', tec: 'PostgreSQL (la fuente de verdad)', detalle: 'TODA la lógica de negocio vive en SQL: RPCs SECURITY DEFINER + Row-Level Security multi-tenant por empresa' },
  { capa: 'Workers', tec: 'Node.js (sri-worker, notificador) + Python (trading-bot)', detalle: 'Firma/envío SRI y SUNAT, descarga del portal, email/WhatsApp/PDF firmados, motor de trading Binance' },
  { capa: 'Infraestructura', tec: 'Apache/nginx + systemd', detalle: 'Local y remoto; los workers se coordinan por LISTEN/NOTIFY de PostgreSQL (sin colas externas)' },
];

const PATRONES: { t: string; d: string }[] = [
  { t: 'Multi-tenant con RLS', d: 'Cada fila lleva empresa_id; Row-Level Security aísla los datos por empresa a nivel del motor — no del código.' },
  { t: 'Lógica en la BD', d: 'RPCs SECURITY DEFINER validan cada FK del payload contra la empresa del JWT: una API imposible de puentear.' },
  { t: 'Saldos serializados', d: 'Stock, cajas y monederos: asegurar fila + FOR UPDATE + delta — dos ventas simultáneas jamás pierden un movimiento.' },
  { t: 'Outbox idempotente', d: 'Ventas offline con UUID de cliente: los reintentos de sincronización no duplican jamás.' },
  { t: 'Emisión offline SRI', d: 'Bloques de secuenciales reservados por caja: la factura nace con clave de acceso válida aun sin internet (72h para transmitir).' },
  { t: 'Rollup incremental', d: 'Dashboards para 100+ locales: triggers mantienen agregados diarios — consultar no degrada la operación.' },
  { t: 'Espejo de redondeo', d: 'El preview del cliente replica el cálculo del servidor a 6 decimales: lo que se muestra es lo que se cobra.' },
  { t: 'Auditoría total', d: 'Soft-delete + triggers de historial (antes/después por campo) en cada tabla de negocio.' },
  { t: 'Secretos cifrados', d: 'Claves SRI, .p12, SMTP y APIs con pgcrypto en reposo; el descifrado solo existe para los workers.' },
  { t: 'Mínimos dinámicos', d: 'La reposición de inventario se calcula de la demanda real y el lead time del proveedor — cero mantenimiento manual.' },
];

const ESQUEMA_DESC: Record<string, string> = {
  autenticacion: 'Núcleo: usuarios, empresas, JWT, menús y permisos por perfil',
  drpro: 'ERP core histórico: tickets, catálogos base',
  financiero: 'Documentos SRI (recibidos/emitidos), facturación electrónica, CxC/CxP, retenciones',
  inventario: 'Productos y variantes, bodegas, WMS (bins), kardex, costo promedio, reposición y OC',
  pos: 'Punto de venta: cajas, sesiones, multipago, lealtad, devoluciones, modo offline',
  contabilidad: 'Plan de cuentas, asientos automáticos, centros/proyectos, cierres y estados financieros',
  control_bancario: 'Bancos, conciliación, cheques, caja chica y tesorería',
  nomina: 'Payroll Ecuador: contratos firmados, rol de pagos, IESS, beneficios y portal del empleado',
  produccion: 'BOM con factor de talla, órdenes de trabajo, WIP y maquila',
  activofijo: 'Activos NIC 16: depreciación, revalorización, deterioro y bajas',
  medical: 'Centro médico: pacientes, agenda, historia clínica, recetas y recordatorios',
  crm: 'Gestión comercial y precios',
  trading: 'Bot de trading Binance (cuenta global, testnet primero)',
  mision: 'Atenciones de misión médica',
};

const MODULOS: { nombre: string; menu: string; flujo: string[] }[] = [
  { nombre: 'Punto de Venta', menu: 'POS', flujo: ['Apertura de caja', 'Venta por teclado (online u OFFLINE)', 'Factura con clave SRI al instante', 'Arqueo a ciegas', 'Conciliación a 3 vías', 'Asiento contable automático'] },
  { nombre: 'Inventario + WMS', menu: 'Inventario', flujo: ['Orden de compra / recepción SRI', 'Putaway a bins', 'Kardex + costo promedio', 'Mínimos dinámicos', 'Sugerencia de reposición', 'Transferencia o compra 1-clic'] },
  { nombre: 'Financiero / SRI', menu: 'Financiero', flujo: ['Descarga automática del SRI', 'Clasificación de documentos', 'Facturación electrónica', 'Cartera CxC / CxP', 'Retenciones y NC'] },
  { nombre: 'Contabilidad', menu: 'Contabilidad', flujo: ['Asientos automáticos por módulo', 'Centros de costo y proyectos', 'Períodos y cierre', '5 estados financieros', 'Anexos F104 / F103'] },
  { nombre: 'Control Bancario', menu: 'Control Bancario', flujo: ['Movimientos y depósitos', 'Conciliación bancaria', 'Cheques y caja chica', 'Tesorería y aprobaciones'] },
  { nombre: 'Nómina', menu: 'Nómina', flujo: ['Contratos PDF firmados (.p12 + QR)', 'Asistencia y novedades', 'Rol de pagos (motor legal EC)', 'IESS y Formulario 107', 'Portal del empleado'] },
  { nombre: 'Producción', menu: 'Producción', flujo: ['Receta (BOM) + factor talla', 'Orden de trabajo interna/maquila', 'Consumo MP → WIP', 'Entrada de producto terminado', 'Cierre con merma real'] },
  { nombre: 'Activo Fijo', menu: 'Activo Fijo', flujo: ['Alta y componentes', 'Depreciación NIC 16', 'Revalorización / deterioro', 'Toma física', 'Bajas y ventas'] },
  { nombre: 'Centro Médico', menu: 'Centro Medico', flujo: ['Agenda y admisión', 'Historia clínica', 'Recetas y certificados', 'Facturación', 'Recordatorios Email/WhatsApp'] },
  { nombre: 'Trading', menu: 'Trading', flujo: ['Configuración y llaves cifradas', 'Motor Python (ccxt)', 'Riesgo + kill switch', 'Monitoreo en el ERP'] },
];

const OFFLINE_PASOS: { icon: 'off' | 'on'; t: string; d: string }[] = [
  { icon: 'off', t: '1 · Se va la luz / internet', d: 'El indicador pasa a OFFLINE. La app ya está instalada (PWA) y el catálogo completo vive en el navegador (IndexedDB).' },
  { icon: 'off', t: '2 · Se sigue vendiendo', d: 'Escaneo y búsqueda locales, solo efectivo. La factura toma un secuencial del bloque reservado y su clave de acceso se calcula en el equipo.' },
  { icon: 'off', t: '3 · Cola local', d: 'Cada venta queda en el outbox con un UUID único; el ticket se imprime normal con serie y clave.' },
  { icon: 'on', t: '4 · Vuelve la conexión', d: 'La cola se sincroniza sola: el servidor re-valida cada clave, aplica stock, kardex, contabilidad y fidelidad con la hora real de venta.' },
  { icon: 'on', t: '5 · Sin duplicados', d: 'Si un reintento repite una venta, el servidor la reconoce por su UUID y responde la original: idempotencia total.' },
];

// ---------- componentes de diagrama (CSS puro) ----------
function CajaDiag({ titulo, sub, tone = 'default' }: { titulo: string; sub?: string; tone?: 'default' | 'primary' | 'muted' }) {
  const cls = tone === 'primary' ? 'border-primary/60 bg-primary/10' : tone === 'muted' ? 'border-dashed' : 'bg-muted/40';
  return (
    <div className={`rounded-lg border px-4 py-2.5 text-center ${cls}`}>
      <div className="text-sm font-semibold">{titulo}</div>
      {sub && <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">{sub}</div>}
    </div>
  );
}
const Flecha = ({ label }: { label?: string }) => (
  <div className="flex flex-col items-center text-muted-foreground">
    {label && <span className="text-[10px]">{label}</span>}
    <span className="text-lg leading-none">↓</span>
  </div>
);

export function ArquitecturaSistema() {
  const { data, isFetching } = useQuery<Estado>({
    queryKey: ['sys-arquitectura'],
    queryFn: async () => (await apiClient.post('/rpc/sys_arquitectura_estado', {})).data,
    staleTime: 60_000,
  });

  if (!data) {
    return <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Leyendo la arquitectura en vivo…</div>;
  }
  const t = data.totales;
  const kpis: { icon: typeof Database; label: string; valor: string }[] = [
    { icon: Layers, label: 'Módulos (menús)', valor: num(t.menus) },
    { icon: Boxes, label: 'Pantallas', valor: num(t.pantallas) },
    { icon: Database, label: 'Esquemas de BD', valor: num(t.esquemas) },
    { icon: Database, label: 'Tablas', valor: num(t.tablas) },
    { icon: Workflow, label: 'Funciones (RPC/SQL)', valor: num(t.funciones) },
    { icon: Globe, label: 'Empresas (tenants)', valor: num(t.empresas) },
  ];

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <Network className="w-5 h-5" /> Arquitectura del sistema
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground max-w-3xl">
          ERP multi-empresa donde <span className="font-medium text-foreground">toda la lógica vive en la base de datos</span>:
          el navegador habla directo con PostgreSQL a través de PostgREST, sin backend intermedio.
          Los números de esta página se leen <span className="font-medium text-foreground">en vivo</span> de la BD
          (PostgreSQL {data.postgres_version}) — esta documentación no puede quedar desactualizada.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <Card key={k.label}><CardContent className="pt-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><k.icon className="w-4 h-4" /> {k.label}</div>
            <div className="text-2xl font-semibold font-mono tabular-nums mt-1">{k.valor}</div>
          </CardContent></Card>
        ))}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Cpu className="w-4 h-4" /> Arquitectura (vista de contenedores)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-4 lg:grid-cols-[1fr_16rem]">
            <div className="space-y-1.5">
              <CajaDiag titulo="Navegador — React 18 + TypeScript (SPA / PWA)" sub="Pestañas keep-alive · TanStack Query · modo OFFLINE con IndexedDB y cola de ventas" />
              <Flecha label="HTTPS · JSON · JWT" />
              <CajaDiag titulo="PostgREST" sub="API REST autogenerada desde la BD — no existe backend intermedio que mantener" />
              <Flecha label="rol app_user según el JWT" />
              <CajaDiag titulo={`PostgreSQL ${data.postgres_version} — LA FUENTE DE VERDAD`} tone="primary"
                sub={`${num(t.esquemas)} esquemas · ${num(t.tablas)} tablas · ${num(t.funciones)} funciones — RPCs SECURITY DEFINER + Row-Level Security multi-tenant`} />
            </div>
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">Workers (LISTEN/NOTIFY)</div>
              <CajaDiag titulo="sri-worker (Node)" sub="Firma XAdES, SRI Ecuador y SUNAT Perú, descarga del portal" tone="muted" />
              <CajaDiag titulo="notificador (Node)" sub="Email/WhatsApp, roles PDF cifrados, contratos firmados" tone="muted" />
              <CajaDiag titulo="trading-bot (Python)" sub="Binance vía ccxt, kill switch en BD" tone="muted" />
              <div className="text-xs font-medium text-muted-foreground pt-1">Integraciones externas</div>
              <div className="flex flex-wrap gap-1.5">
                {['SRI', 'SUNAT', 'De Una (Pichincha)', 'Broadnet', 'Twilio WhatsApp', 'SMTP', 'Binance'].map((x) => (
                  <span key={x} className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground">{x}</span>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Stack tecnológico</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Capa</th><th className="py-2 pr-3">Tecnología</th><th className="py-2 pr-3">Detalle</th>
            </tr></thead>
            <tbody>
              {STACK.map((r) => (
                <tr key={r.capa} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium whitespace-nowrap">{r.capa}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{r.tec}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{r.detalle}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Patrones de diseño</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {PATRONES.map((p) => (
            <div key={p.t} className="rounded-lg border p-3">
              <div className="text-sm font-semibold">{p.t}</div>
              <div className="text-xs text-muted-foreground mt-1">{p.d}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4" /> Mapa de la base de datos <span className="text-xs font-normal text-muted-foreground">(conteos en vivo)</span></CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {data.esquemas.map((e) => (
            <div key={e.nombre} className="rounded-lg border p-3">
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm font-semibold">{e.nombre}</span>
                <span className="text-[11px] text-muted-foreground tabular-nums">{num(e.tablas)} tablas · {num(e.funciones)} fn</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">{ESQUEMA_DESC[e.nombre] ?? 'Esquema de dominio'}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Workflow className="w-4 h-4" /> Módulos y su flujo</CardTitle></CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-2">
          {MODULOS.map((mod) => {
            const vivo = data.menus.find((x) => x.nombre === mod.menu);
            return (
              <div key={mod.nombre} className="rounded-lg border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{mod.nombre}</span>
                  {vivo && <span className="text-[11px] text-muted-foreground tabular-nums">{num(vivo.pantallas)} pantallas</span>}
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-1 text-[11px]">
                  {mod.flujo.map((paso, i) => (
                    <span key={paso} className="flex items-center gap-1">
                      <span className="rounded bg-muted/60 px-1.5 py-0.5">{paso}</span>
                      {i < mod.flujo.length - 1 && <span className="text-muted-foreground">→</span>}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><WifiOff className="w-4 h-4" /> Modo offline (cortes de energía / internet)</CardTitle></CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-5">
            {OFFLINE_PASOS.map((p) => (
              <div key={p.t} className={`rounded-lg border p-3 ${p.icon === 'off' ? 'border-red-300/60' : 'border-emerald-300/60'}`}>
                <div className="flex items-center gap-1.5 text-sm font-semibold">
                  {p.icon === 'off' ? <WifiOff className="w-3.5 h-3.5 text-red-500" /> : <Wifi className="w-3.5 h-3.5 text-emerald-500" />}
                  {p.t}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{p.d}</div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Emisión offline contemplada por el SRI (72 h para transmitir). Requiere equipo con batería/UPS;
            sin red solo se acepta efectivo y se pausan promociones, canjes y cobros QR.
          </p>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Generado en vivo el {new Date(data.generado_at).toLocaleString(LOCALE)} · PostgreSQL {data.postgres_version} ·
        {` ${num(t.usuarios)} usuarios en ${num(t.empresas)} empresas.`}
      </p>
    </div>
  );
}
